import {
    KeyboardEvent,
    ClipboardEvent,
    useState,
    useRef,
    useEffect
} from 'react';
import { ILoginReferrer, login } from '../../dal/data-provider';
import { IServerValidation } from '../../../../domain/validation/interfaces';
import ValidationErrors from '../../../../domain/validation/server-errors/validation-errors';
import {
    createControl,
    updateControl,
    validateForm,
    removeInvisibleCharsOnPaste,
    validateControl
} from '../../../../domain/validation/validation';
import { getSimpleCaptchaValidationError } from '../../../../domain/captcha/simple-captcha';
import { getIsChina, getIsAuthenticated } from '../../../../domain/hidden-data-provider';
import { getRecaptchaToken, renderRecaptcha2, resetRecaptcha2, getRecaptcha2Error } from '../../../../domain/captcha/recaptcha-provider';
import Recaptcha3Text from '../../../../domain/captcha/recaptcha3-terms';
import LogoutScreen from '../../logout/logout-screen';
import { updateUserPurchasedData } from '../../purchased-products/user-purchased-products';
import { IFormControl } from '../../../../ui/forms/interfaces';
import FormTextField from '../../../../ui/forms/fields/text-field';
import FormCheckboxField from '../../../../ui/forms/fields/checkbox-field';
import ChinaCaptchaField from '../../../../ui/forms/fields/captcha-china-field';
import FormTitle from '../../../../ui/forms/form-title';
import FormBorder from '../../../../ui/forms/form-border';
import FormPreloader from '../../../../ui/forms/form-preloader';
import { rvpSetNullToStorage } from '../../../recently-viewed-products/data/data-service';
import ExternalLogin from '../external-login/external-login';

/**
 * once user is logged in, redirect the page to the previous page, or to the user account
 */
export const redirectAfterLogin = () => {

    const search = window.location.search;
    let newURL = '/account';

    if(search.toLowerCase().startsWith('?returnurl')){

        const decoded = window.decodeURIComponent(search);
        const url = new URL(window.location.href.toLowerCase());
        const returnUrlParam = url.searchParams.get('returnurl') || '';

        // prevent XSS - verify that return url is a valid url, and
        // it should start with a single slash,
        // and after it the rest can be anything except a 2nd slash right after the first one.
        let isValidUrl = true;

        if(!returnUrlParam.startsWith('/') || returnUrlParam.startsWith('//')){
            isValidUrl = false;
        }

        if(isValidUrl){
            try{
                const uri = `https://${ window.location.host }${ returnUrlParam }`;
                const returnUrl = new URL(uri);
                console.log('returnUrl', returnUrl);
            }
            catch(ex){
                isValidUrl = false;
                console.error(ex);
            }
        }

        if(isValidUrl){
            newURL = decoded.replace(/\?returnurl=/gi, '');
        }
    }

    // remove current page from history,
    // so when user clicks 'back button' it will skip the login page
    window.location.replace(newURL);
};

/**
 * Login app
 * @constructor
 */
const Login = () => {

    const userNameRef = useRef<HTMLInputElement>(null); // used for focus
    const isChina = getIsChina();
    const isUserAuth = getIsAuthenticated();

    // validation ---------------------------------------
    const userNameControl = createControl('userName', '', {
        required: true
    });
    const passwordControl = createControl('password', '', {
        required: true
    });
    const simpleCaptchaControl = createControl('simpleCaptcha', '', {
        required: true,
        requiredMessage: getSimpleCaptchaValidationError(),
        length: 5,
    });

    const [isLoading, setIsLoading] = useState(false);
    const [response, setResponse] = useState<IServerValidation|null>();
    const [userName, setUserName] = useState<IFormControl>(userNameControl);
    const [password, setPassword] = useState<IFormControl>(passwordControl);
    const [isPersistent, setIsPersistent] = useState(true);

    // simple captcha - used for chine
    const [simpleCaptchaCode, setSimpleCaptchaCode] = useState<IFormControl>(simpleCaptchaControl);
    const [simpleCaptchaAttempts, setSimpleCaptchaAttempts] = useState(0);

    // google recaptcha version 2 & 3
    const [recaptchaVersion, setRecaptchaVersion] = useState(3);
    const [recaptcha2WidgetID, setRecaptcha2WidgetID] = useState(0);

    // captcha: common
    const [captchaError, setCaptchaError] = useState('');

    useEffect(() => {

        // on page load -> focus on the username field
        if(!userNameRef || !userNameRef.current) return;
        userNameRef.current.focus();
    }, []);

    /**
     * if recaptcha 3 returns low score (the limit is defined in kentico settings),
     * then recaptcha 2 fallback should be rendered inside <div id="recaptcha"></div>
     */
    const renderCaptcha2Fallback = () => {
        if(!window.grecaptcha) return;

        window.grecaptcha.ready(async () => {
            const widgetID = await renderRecaptcha2('recaptcha');
            setRecaptcha2WidgetID(widgetID);
        });
    };

    /**
     * perform user login on server
     */
    const performLogin = async () => {

        // client side validation
        const isValid =
            isChina ?
            validateForm([
                [userName, setUserName],
                [password, setPassword],
                [simpleCaptchaCode, setSimpleCaptchaCode],
            ]) :
            validateForm([
                [userName, setUserName],
                [password, setPassword],
            ]);

        if(!isValid) return;

        setIsLoading(true);

        let response = null;

        try {
            const captchaToken = !isChina ? await getRecaptchaToken(recaptchaVersion) : simpleCaptchaCode.value;

            if(!captchaToken){
                setCaptchaError(isChina ? getSimpleCaptchaValidationError() : getRecaptcha2Error());
                setIsLoading(false);
                return;
            }

            response = await login(
                ILoginReferrer.LoginPage,
                userName.value,
                password.value,
                isPersistent,
                captchaToken,
                recaptchaVersion
            );
        }
        catch(ex){}

        if(
            response &&
            response.statusCode === 200 &&
            response.data &&
            response.data.isValid){

            // all is well ---------------

            // reset and reload user purchased products
            await updateUserPurchasedData();

            // clear recently viewed products storage
            rvpSetNullToStorage();

            // perform redirect
            redirectAfterLogin();

            return;
        }

        // handle errors ---------

        setIsLoading(false);

        if(isChina){

            // re-render simple captcha
            setSimpleCaptchaAttempts(simpleCaptchaAttempts + 1);
        }
        else{
            if(response?.data.relatedData === 'recaptcha-error') {
                setRecaptchaVersion(2);
                renderCaptcha2Fallback();
            }

            if(recaptchaVersion === 2){
                resetRecaptcha2();
            }
        }

        // render errors
        setResponse(response?.data);
    };

    /**
     * and enter / return keys
     */
    const handleEnter = async (evt: KeyboardEvent<HTMLInputElement>) => {
        if(evt.key === 'Enter') {
            await performLogin();
        }
    };

    /**
     * empty errors on click
     */
    const handleFieldClick = () =>{
        setResponse(null);
        setCaptchaError('');
    };

    return (
        <>
            {
                !isUserAuth &&
                <FormBorder>
                    <FormTitle title={ 'Log in to your Waves account' } />

                    <ExternalLogin
                        facebookText="Sign in with Facebook"
                        googleTextParam="signin_with"
                        dividerText="Or, sign in with your username"
                    />

                    <FormTextField
                        id={ 'username-tb' }
                        label={ 'Username' }
                        control={ userName }
                        maxLength={ 100 }
                        qa={ 'login-username' }
                        onChange={ evt => {
                            const $target = evt.target as HTMLInputElement;
                            setUserName(updateControl(userName, $target.value, !userName.isValid));
                            setResponse(null); // empty server error
                            setCaptchaError('');
                        }}
                        onBlur={ () => {
                            setUserName(validateControl(userName));
                        }}
                        onClick={ handleFieldClick }
                        onKeyUp={ handleEnter }
                        onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                            setUserName(updateControl(userName, removeInvisibleCharsOnPaste(evt)));
                        }}
                    />

                    <FormTextField
                        id={ 'password-tb' }
                        label={ 'Password' }
                        control={ password }
                        maxLength={ 110 }
                        qa={ 'login-password' }
                        type={ 'password' }
                        onChange={ evt => {
                            const $target = evt.target as HTMLInputElement;
                            setPassword(updateControl(password, $target.value, !password.isValid));
                            setResponse(null); // empty server error
                            setCaptchaError('');
                        }}
                        onBlur={ () => {
                            setPassword(validateControl(password));
                        }}
                        onClick={ handleFieldClick }
                        onKeyUp={ handleEnter }
                        onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                            setPassword(updateControl(password, removeInvisibleCharsOnPaste(evt)));
                        }}
                    />

                    <FormCheckboxField
                        id={ 'login-remember-me-cb' }
                        label={ 'Remember me' }
                        isChecked={ isPersistent }
                        onChange={ evt => {
                            setIsPersistent(val => !val);
                        }}
                        onClick={ handleFieldClick }
                        additionalContent={
                            <div className="flex items-center">
                                <a
                                    className="text-sm no-underline"
                                    href="/forgot-password"
                                    data-qa="login-forgot-password"
                                    title="">Forgot Password?</a>
                            </div>
                        }
                    />

                    {
                        /* ------------- google recaptcha version 2 or 3 ------------- */
                        !isChina &&
                        <div id="recaptcha" className={`mb-4 flex justify-center recaptcha recaptcha-version-${ recaptchaVersion }`}/>
                    }

                    {
                        /* ------------- china simple captcha ------------- */
                        isChina &&
                        <ChinaCaptchaField
                            control={ simpleCaptchaCode }
                            setSimpleCaptchaCode={ setSimpleCaptchaCode }
                            simpleCaptchaAttempts={ simpleCaptchaAttempts }
                        />
                    }

                    <div className="mb-2">

                        { isLoading && <FormPreloader /> }

                        {
                            !isLoading &&
                            <button
                                className="w-btn"
                                type="button"
                                data-qa="login-button"
                                onClick={async evt => {
                                    await performLogin();
                                }}
                            >Log in</button>
                        }
                    </div>

                    {
                        response && !response.isValid && (
                            <div className="text-sm text-red my-4 text-center">
                                <ValidationErrors errors={ response.errors } />
                            </div>
                        )
                    }

                    {
                        captchaError &&
                        <div className="text-sm text-red my-4 text-center" data-id={ recaptcha2WidgetID }>{ captchaError }</div>
                    }

                    <div className="text-center">
                        <a className="text-sm no-underline" href="/create-account" title="">Create New Account</a>
                    </div>

                    {
                        !isChina && recaptchaVersion === 3 &&
                        <div className="mt-8 text-center">
                            <Recaptcha3Text />
                        </div>
                    }
                </FormBorder>
            }

            { isUserAuth && <LogoutScreen /> }

        </>

    )
};

export default Login;